import React, { useState, useRef, Component } from 'react';
import { Settings, LogOut, AlertCircle, Check, ChevronDown, CreditCard, HelpCircle, Book, User, Link, PlayCircle, Sparkles, Search, Linkedin, ArrowRight, Lock, CheckCircle, ChevronRight, MessagesSquare, ChevronUp, Edit, Save, Filter, Trash2, Target, Loader } from 'lucide-react';
import { ChatWindow } from './ChatWindow';
import { ProspectList } from './ProspectList';
import { LinkedInFilters } from './LinkedInFilters';
import { ScheduleModal } from './ScheduleModal';
import { NewCampaignModal } from './NewCampaignModal';
interface DashboardLayoutProps {
  children: React.ReactNode;
}
export const DashboardLayout = ({
  children
}: DashboardLayoutProps) => {
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [campaignName, setCampaignName] = useState('Campaign 1');
  const [isLinkedInConnected, setIsLinkedInConnected] = useState(false);
  const [selectedDepth, setSelectedDepth] = useState(3); // Fixed at 3
  const [selectedAccuracy, setSelectedAccuracy] = useState(9); // Fixed at 9
  const [chatMessage, setChatMessage] = useState('');
  const [showProspects, setShowProspects] = useState(false);
  const [showConnectLinkedInPrompt, setShowConnectLinkedInPrompt] = useState(false);
  const [currentConnectionStep, setCurrentConnectionStep] = useState(1);
  const [linkedinFilters, setLinkedinFilters] = useState(['Industry: Technology', 'Company size: 50-200', 'Location: United States']);
  const [showSystemPromptModal, setShowSystemPromptModal] = useState(false);
  const [isLinkedInFiltersMinimized, setIsLinkedInFiltersMinimized] = useState(false);
  const [isSystemPromptMinimized, setIsSystemPromptMinimized] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [targetProspectCount, setTargetProspectCount] = useState(25);
  const [isNewCampaignModalOpen, setIsNewCampaignModalOpen] = useState(false);
  const [chatKey, setChatKey] = useState(0); // Add key to force ChatWindow re-render
  const startNewCampaign = () => {
    // Open the modal to ask for campaign name
    setIsNewCampaignModalOpen(true);
  };
  const handleCreateCampaign = (name: string) => {
    // Set the new campaign name
    setCampaignName(name);
    // Reset all dashboard states to reload
    setShowProspects(false);
    setIsLinkedInConnected(false);
    // Clear product description by resetting chatMessage
    setChatMessage('');
    // Clear LinkedIn filters to empty array
    setLinkedinFilters([]);
    setSelectedDepth(1);
    setSelectedAccuracy(7);
    // Force ChatWindow to re-render with empty content
    setChatKey(prev => prev + 1);
    // Close the modal
    setIsNewCampaignModalOpen(false);
  };
  const defaultSystemPrompt = `You are an AI assistant designed to identify and connect with potential prospects on LinkedIn based on the product description and filters provided. Your task is to:
1. Analyze the product description to understand the key value propositions
2. Use the LinkedIn filters to narrow down the target audience
3. Generate a list of potential prospects that would be a good fit for the product
4. Create personalized connection messages for each prospect based on their profile
5. Ensure high accuracy and relevance in your prospect matching
Focus on finding decision-makers and individuals who would directly benefit from the product or service described. Prioritize prospects based on their likelihood to engage and convert.

Based on each prospect's personality type, customize your messaging approach:
- For Analytical prospects: Focus on data, metrics, and proven results
- For Collaborative prospects: Emphasize teamwork benefits and shared success
- For Decisive prospects: Be direct, concise, and highlight clear ROI
- For Innovative prospects: Showcase cutting-edge aspects and forward-thinking benefits
- For Detail-oriented prospects: Provide specific information and comprehensive explanations
- For Results-driven prospects: Emphasize concrete outcomes and performance improvements
- For Strategic prospects: Connect the solution to long-term goals and competitive advantage
- For Practical prospects: Focus on implementation ease and immediate utility
- For Visionary prospects: Highlight transformative potential and future possibilities
- For Relationship-focused prospects: Emphasize partnership value and ongoing support`;
  const [systemPrompt, setSystemPrompt] = useState(defaultSystemPrompt);
  const prospectListRef = useRef(null);
  const handleChatChange = (message: string) => {
    setChatMessage(message);
    // Simple mockup behavior to update filters based on keywords in the message
    const newFilters = [];
    if (message.toLowerCase().includes('cloud')) {
      newFilters.push('Industry: Cloud Computing');
    }
    if (message.toLowerCase().includes('enterprise')) {
      newFilters.push('Company size: 1000+');
    }
    if (message.toLowerCase().includes('cost')) {
      newFilters.push('Role: CFO, Finance Director');
    }
    if (message.toLowerCase().includes('infrastructure')) {
      newFilters.push('Role: CTO, IT Director');
    }
    if (message.toLowerCase().includes('ai')) {
      newFilters.push('Technology: AI/ML');
    }
    if (newFilters.length > 0) {
      setLinkedinFilters(newFilters);
    } else {
      setLinkedinFilters(['Industry: Technology', 'Company size: 50-200', 'Location: United States']);
    }
  };
  // Handle adding a new filter
  const handleAddFilter = (filter: string) => {
    setLinkedinFilters([...linkedinFilters, filter]);
  };
  // Handle removing a filter
  const handleRemoveFilter = (index: number) => {
    const newFilters = [...linkedinFilters];
    newFilters.splice(index, 1);
    setLinkedinFilters(newFilters);
  };
  const handleStartCampaign = () => {
    setIsScheduleModalOpen(true);
  };
  const handleSchedule = (startDate: Date, depthDays: number) => {
    console.log('Schedule:', {
      startDate,
      depthDays
    });
    setIsScheduleModalOpen(false);
    // Start the campaign scheduling process
    if (prospectListRef?.current?.startCampaignAfterSchedule) {
      prospectListRef.current.startCampaignAfterSchedule();
    }
  };
  const handleCampaignScheduled = () => {
    // Don't reopen the schedule modal after campaign completion
    // The progress bars will remain visible
    console.log('Campaign completed');
  };
  const handleGenerateProspects = () => {
    // Show prospects immediately when button is clicked
    setShowProspects(true);
    if (!isLinkedInConnected) {
      setShowConnectLinkedInPrompt(true);
      setCurrentConnectionStep(1);
      return;
    }
    if (prospectListRef?.current?.handleGenerateProspects) {
      prospectListRef.current.handleGenerateProspects();
    }
  };
  const goToNextStep = () => {
    setCurrentConnectionStep(prev => {
      if (prev === 3) {
        connectLinkedIn();
        return 1;
      }
      return prev + 1;
    });
  };
  const connectLinkedIn = () => {
    setIsLinkedInConnected(true);
    setShowConnectLinkedInPrompt(false);
    // After connecting, start generating prospects
    setTimeout(() => {
      if (prospectListRef?.current?.handleGenerateProspects) {
        prospectListRef.current.handleGenerateProspects();
        setShowProspects(true);
      }
    }, 300);
  };
  const MenuValue = ({
    children
  }: {
    children: React.ReactNode;
  }) => <span className="text-sm font-medium text-gray-900" data-id="element-354">{children}</span>;
  const MenuItem = ({
    label,
    value,
    options = [],
    onChange,
    showNumbers = false
  }: any) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const handleInputBlur = () => {
      onChange(editValue);
      setIsEditing(false);
    };
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        onChange(editValue);
        setIsEditing(false);
      } else if (e.key === 'Escape') {
        setEditValue(value);
        setIsEditing(false);
      }
    };
    const handleOptionSelect = option => {
      if (option === 'New Campaign') {
        startNewCampaign();
      } else {
        onChange(option);
      }
    };
    // Make sure New Campaign is the last option
    const sortedOptions = [...options].sort((a, b) => {
      if (a === 'New Campaign') return 1;
      if (b === 'New Campaign') return -1;
      return 0;
    });
    return <div className="relative group" data-id="element-355">
        <div className="flex items-center" data-id="element-356">
          <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md" onClick={() => setIsEditing(true)} data-id="element-357">
            {isEditing ? <input type="text" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={handleInputBlur} onKeyDown={handleKeyDown} className="px-1 py-0.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6366f1] focus:border-transparent outline-none" autoFocus data-id="element-358" /> : <span data-id="element-359">{label || value}</span>}
            <ChevronDown className="h-4 w-4 text-gray-500" data-id="element-360" />
          </button>
          {/* Delete icon for campaigns */}
          {value.includes('Campaign') && <button className="ml-2 p-1 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100" onClick={() => {
          // Show confirmation before deleting
          if (window.confirm(`Are you sure you want to delete ${value}?`)) {
            // If this is the only campaign, create a new one
            if (options.length <= 1) {
              startNewCampaign();
            } else {
              // Otherwise select another campaign
              const otherCampaign = options.find(opt => opt !== value && opt !== 'New Campaign');
              if (otherCampaign) {
                onChange(otherCampaign);
              }
            }
          }
        }} data-id="element-361">
              <Trash2 size={16} data-id="element-362" />
            </button>}
        </div>
        <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-100 hidden group-hover:block z-50" data-id="element-363">
          <div className="py-1" data-id="element-364">
            {sortedOptions.map((option: any) => <button key={option.display || option} onClick={() => handleOptionSelect(option.value || option)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50" data-id="element-365">
                {option.display || option}
              </button>)}
          </div>
        </div>
      </div>;
  };
  const getDotsForNumber = (num: number) => '•'.repeat(num);
  return <div className="min-h-screen bg-gray-50" data-id="element-366">
      <nav className="bg-white shadow-sm" data-id="element-367">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-368">
          <div className="flex justify-between h-16" data-id="element-369">
            <div className="flex items-center space-x-8" data-id="element-370">
              <MenuItem value={campaignName} options={['Campaign 1', 'Campaign 2', 'Campaign 3', 'New Campaign']} onChange={setCampaignName} data-id="element-371" />
              {isLinkedInConnected && <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-green-50 text-green-700" data-id="element-372">
                  <Linkedin className="h-5 w-5" data-id="element-373" />
                  <Check className="h-4 w-4" data-id="element-374" />
                  <span className="text-sm font-medium" data-id="element-375">
                    LinkedIn Connected
                  </span>
                </div>}
            </div>
            <div className="flex items-center space-x-4" data-id="element-376">
              <div className="relative group" data-id="element-377">
                <button className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-full" data-id="element-378">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/98HoHnaDpqofrTai9w3RpF/lucian_cretu.jpg" alt="User" className="h-8 w-8 rounded-full object-cover" data-id="element-379" />
                  <ChevronDown className="h-4 w-4 text-gray-500" data-id="element-380" />
                </button>
                <div className="absolute right-0 mt-2 w-64 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-gray-100 py-1 hidden group-hover:block z-[9999]" data-id="element-381">
                  <div className="px-4 py-2 border-b border-gray-100" data-id="element-382">
                    <p className="font-medium text-gray-900" data-id="element-383">Lucian Cretu</p>
                    <p className="text-sm text-gray-500" data-id="element-384">
                      Pro Plan - 87 credits
                    </p>
                  </div>
                  <div className="py-1" data-id="element-385">
                    <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50/90 flex items-center gap-2" data-id="element-386">
                      <CreditCard className="h-4 w-4" data-id="element-387" />
                      Billing & Subscription
                    </button>
                    <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50/90 flex items-center gap-2" data-id="element-388">
                      <Book className="h-4 w-4" data-id="element-389" />
                      Documentation
                    </button>
                    <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50/90 flex items-center gap-2" data-id="element-390">
                      <HelpCircle className="h-4 w-4" data-id="element-391" />
                      Get Help
                    </button>
                    <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50/90 flex items-center gap-2" data-id="element-392">
                      <LogOut className="h-4 w-4" data-id="element-393" />
                      Log Out
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-394">
        <div className="space-y-6" data-id="element-395">
          <div className="relative" data-id="element-396">
            <ChatWindow key={chatKey} title="Campaign Description" placeholder="Describe your campaign or target audience..." initialMessage="" detailLevel={3} icon={<Sparkles className="h-4 w-4 text-purple-500" data-id="element-398" />} onMessageChange={handleChatChange} initialContent={chatMessage} data-id="element-397" />
          </div>
          {/* LinkedIn Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col" data-id="element-399">
            <div className="p-3 border-b border-gray-200 flex items-center justify-between cursor-pointer" onClick={() => setIsLinkedInFiltersMinimized(!isLinkedInFiltersMinimized)} data-id="element-400">
              <div className="flex items-center gap-2" data-id="element-401">
                <Filter className="h-4 w-4 text-purple-500" data-id="element-402" />
                <h3 className="font-medium text-gray-700" data-id="element-403">LinkedIn Filters</h3>
              </div>
              {isLinkedInFiltersMinimized ? <ChevronDown className="h-4 w-4 text-gray-500" data-id="element-404" /> : <ChevronUp className="h-4 w-4 text-gray-500" data-id="element-405" />}
            </div>
            {!isLinkedInFiltersMinimized && <div className="p-4" data-id="element-406">
                <LinkedInFilters initialFilters={linkedinFilters} onAddFilter={handleAddFilter} onRemoveFilter={handleRemoveFilter} data-id="element-407" />
              </div>}
          </div>
          {/* System Prompt Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col" data-id="element-408">
            <div className="p-3 border-b border-gray-200 flex items-center justify-between cursor-pointer" onClick={() => setIsSystemPromptMinimized(!isSystemPromptMinimized)} data-id="element-409">
              <div className="flex items-center gap-2" data-id="element-410">
                <Sparkles className="h-4 w-4 text-blue-500" data-id="element-411" />
                <h3 className="font-medium text-gray-700" data-id="element-412">System Prompt</h3>
                <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-md" data-id="element-413">
                  Power User
                </span>
              </div>
            </div>
            {!isSystemPromptMinimized && <div className="p-4" data-id="element-414">
                <textarea value={systemPrompt} onChange={e => setSystemPrompt(e.target.value)} className="w-full h-32 p-3 bg-gray-50 rounded-md text-sm text-gray-700 resize-none focus:ring-2 focus:ring-blue-500 focus:outline-none border border-gray-300" data-id="element-415" />
              </div>}
          </div>

          {/* Discover Button - Before ProspectList */}
          {!isLinkedInConnected && <div className="flex flex-col items-center py-6 gap-4" data-id="element-416">
              <button onClick={handleGenerateProspects} className="px-6 py-3 bg-[#d5ff00] text-gray-900 hover:bg-[#c0e600] rounded-lg font-semibold flex items-center gap-2 shadow-sm transition-colors" data-id="element-417">
                <Search size={20} data-id="element-418" />
                Discover Qualified Prospects
              </button>
            </div>}

          <ProspectList productDescription={chatMessage} linkedinFilters={linkedinFilters} onStartCampaign={handleStartCampaign} onCampaignScheduled={handleCampaignScheduled} ref={prospectListRef} selectedDepth={selectedDepth} selectedAccuracy={selectedAccuracy} onDepthChange={setSelectedDepth} onAccuracyChange={setSelectedAccuracy} data-id="element-419" />

          {/* LinkedIn Connection Workflow Modal */}
          {showConnectLinkedInPrompt && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-420">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full" data-id="element-421">
                <div className="flex justify-between items-center mb-6" data-id="element-422">
                  <h3 className="text-xl font-medium text-gray-900" data-id="element-423">
                    Connect LinkedIn to Discover Prospects
                  </h3>
                  <button onClick={() => setShowConnectLinkedInPrompt(false)} className="text-gray-400 hover:text-gray-500" data-id="element-424">
                    &times;
                  </button>
                </div>
                {/* Progress Steps */}
                <div className="mb-8" data-id="element-425">
                  <div className="flex items-center justify-between mb-4" data-id="element-426">
                    <div className="flex items-center" data-id="element-427">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentConnectionStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-428">
                        1
                      </div>
                      <div className="ml-2" data-id="element-429">
                        <p className={`font-medium ${currentConnectionStep >= 1 ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-430">
                          Connect
                        </p>
                      </div>
                    </div>
                    <div className="flex-1 mx-4 h-1 bg-gray-200" data-id="element-431">
                      <div className={`h-1 bg-blue-600 transition-all duration-300`} style={{
                    width: `${currentConnectionStep > 1 ? '100%' : '0%'}`
                  }} data-id="element-432"></div>
                    </div>
                    <div className="flex items-center" data-id="element-433">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentConnectionStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-434">
                        2
                      </div>
                      <div className="ml-2" data-id="element-435">
                        <p className={`font-medium ${currentConnectionStep >= 2 ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-436">
                          Permissions
                        </p>
                      </div>
                    </div>
                    <div className="flex-1 mx-4 h-1 bg-gray-200" data-id="element-437">
                      <div className={`h-1 bg-blue-600 transition-all duration-300`} style={{
                    width: `${currentConnectionStep > 2 ? '100%' : '0%'}`
                  }} data-id="element-438"></div>
                    </div>
                    <div className="flex items-center" data-id="element-439">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentConnectionStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-440">
                        3
                      </div>
                      <div className="ml-2" data-id="element-441">
                        <p className={`font-medium ${currentConnectionStep >= 3 ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-442">
                          Verify
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Step Content */}
                <div className="mb-8" data-id="element-443">
                  {currentConnectionStep === 1 && <div className="text-center" data-id="element-444">
                      <div className="flex justify-center mb-6" data-id="element-445">
                        <Linkedin className="h-20 w-20 text-blue-600" data-id="element-446" />
                      </div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4" data-id="element-447">
                        Connect your LinkedIn account
                      </h4>
                      <p className="text-gray-600 mb-6" data-id="element-448">
                        To discover qualified prospects that match your product
                        description, we need to connect to your LinkedIn
                        account. This allows us to search for and identify
                        potential customers based on your criteria.
                      </p>
                      <div className="bg-blue-50 p-4 rounded-lg mb-6 text-left" data-id="element-449">
                        <h5 className="font-medium text-gray-900 mb-2 flex items-center" data-id="element-450">
                          <Lock className="h-4 w-4 mr-2 text-blue-600" data-id="element-451" />
                          Your data is secure
                        </h5>
                        <ul className="text-sm text-gray-600 space-y-2" data-id="element-452">
                          <li className="flex items-start" data-id="element-453">
                            <CheckCircle className="h-4 w-4 mr-2 text-green-500 mt-0.5" data-id="element-454" />
                            We only access the information needed for
                            prospecting
                          </li>
                          <li className="flex items-start" data-id="element-455">
                            <CheckCircle className="h-4 w-4 mr-2 text-green-500 mt-0.5" data-id="element-456" />
                            Your login credentials are never stored
                          </li>
                          <li className="flex items-start" data-id="element-457">
                            <CheckCircle className="h-4 w-4 mr-2 text-green-500 mt-0.5" data-id="element-458" />
                            You can disconnect at any time
                          </li>
                        </ul>
                      </div>
                    </div>}
                  {currentConnectionStep === 2 && <div className="text-center" data-id="element-459">
                      <div className="flex justify-center mb-6" data-id="element-460">
                        <div className="relative" data-id="element-461">
                          <Linkedin className="h-16 w-16 text-blue-600" data-id="element-462" />
                          <div className="absolute -right-2 -bottom-2 bg-blue-600 text-white p-1 rounded-full" data-id="element-463">
                            <Lock className="h-4 w-4" data-id="element-464" />
                          </div>
                        </div>
                      </div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4" data-id="element-465">
                        LinkedIn Permissions
                      </h4>
                      <p className="text-gray-600 mb-6" data-id="element-466">
                        The following permissions will be requested from
                        LinkedIn:
                      </p>
                      <div className="bg-gray-50 p-4 rounded-lg mb-6" data-id="element-467">
                        <div className="space-y-4 text-left" data-id="element-468">
                          <div className="flex items-start" data-id="element-469">
                            <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-470">
                              <User className="h-5 w-5 text-blue-600" data-id="element-471" />
                            </div>
                            <div data-id="element-472">
                              <h5 className="font-medium text-gray-900" data-id="element-473">
                                Profile Access
                              </h5>
                              <p className="text-sm text-gray-600" data-id="element-474">
                                View your basic profile information
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start" data-id="element-475">
                            <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-476">
                              <Search className="h-5 w-5 text-blue-600" data-id="element-477" />
                            </div>
                            <div data-id="element-478">
                              <h5 className="font-medium text-gray-900" data-id="element-479">
                                Search Access
                              </h5>
                              <p className="text-sm text-gray-600" data-id="element-480">
                                Search for prospects matching your criteria
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start" data-id="element-481">
                            <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-482">
                              <MessagesSquare className="h-5 w-5 text-blue-600" data-id="element-483" />
                            </div>
                            <div data-id="element-484">
                              <h5 className="font-medium text-gray-900" data-id="element-485">
                                Messaging Access
                              </h5>
                              <p className="text-sm text-gray-600" data-id="element-486">
                                Send messages to prospects (with your approval)
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>}
                  {currentConnectionStep === 3 && <div className="text-center" data-id="element-487">
                      <div className="flex justify-center mb-6" data-id="element-488">
                        <div className="bg-green-100 p-4 rounded-full" data-id="element-489">
                          <Check className="h-12 w-12 text-green-600" data-id="element-490" />
                        </div>
                      </div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4" data-id="element-491">
                        Verify Your Connection
                      </h4>
                      <p className="text-gray-600 mb-6" data-id="element-492">
                        We'll redirect you to LinkedIn to complete the
                        connection process. After authorizing, you'll be
                        returned to this page automatically.
                      </p>
                      <div className="bg-yellow-50 p-4 rounded-lg mb-6 text-left" data-id="element-493">
                        <h5 className="font-medium text-gray-900 mb-2 flex items-center" data-id="element-494">
                          <AlertCircle className="h-4 w-4 mr-2 text-yellow-600" data-id="element-495" />
                          Important
                        </h5>
                        <p className="text-sm text-gray-600" data-id="element-496">
                          Make sure you're already logged into LinkedIn in your
                          browser before proceeding. This will make the
                          connection process faster and smoother.
                        </p>
                      </div>
                    </div>}
                </div>
                {/* Action Buttons */}
                <div className="flex justify-end gap-3" data-id="element-497">
                  <button onClick={() => setShowConnectLinkedInPrompt(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-498">
                    Cancel
                  </button>
                  {currentConnectionStep < 3 ? <button onClick={goToNextStep} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center" data-id="element-499">
                      Continue
                      <ChevronRight className="h-4 w-4 ml-1" data-id="element-500" />
                    </button> : <button onClick={connectLinkedIn} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center" data-id="element-501">
                      <Linkedin className="h-4 w-4 mr-2" data-id="element-502" />
                      Connect to LinkedIn
                    </button>}
                </div>
              </div>
            </div>}

          {/* System Prompt Modal */}
          {showSystemPromptModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-503">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-3xl w-full" data-id="element-504">
                <div className="flex justify-between items-center mb-4" data-id="element-505">
                  <h3 className="text-xl font-medium text-gray-900" data-id="element-506">
                    Configure AI System Prompt
                  </h3>
                  <button onClick={() => setShowSystemPromptModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-507">
                    &times;
                  </button>
                </div>
                <div className="mb-4" data-id="element-508">
                  <p className="text-sm text-gray-600 mb-2" data-id="element-509">
                    Customize the AI system prompt to control how the AI
                    generates prospects and messages.
                  </p>
                  <textarea value={systemPrompt} onChange={e => setSystemPrompt(e.target.value)} className="w-full h-64 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6366f1] focus:border-transparent outline-none" data-id="element-510" />
                </div>
                <div className="flex justify-between" data-id="element-511">
                  <button onClick={() => {
                setSystemPrompt(defaultSystemPrompt);
              }} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-512">
                    Reset to Default
                  </button>
                  <div className="flex gap-2" data-id="element-513">
                    <button onClick={() => setShowSystemPromptModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-514">
                      Cancel
                    </button>
                    <button onClick={() => {
                  // Save changes
                  setShowSystemPromptModal(false);
                }} className="px-4 py-2 bg-[#6366f1] text-white rounded-md hover:bg-[#5457e5]" data-id="element-515">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>

      <ScheduleModal isOpen={isScheduleModalOpen} onClose={() => setIsScheduleModalOpen(false)} onSchedule={handleSchedule} data-id="element-516" />

      <NewCampaignModal isOpen={isNewCampaignModalOpen} onClose={() => setIsNewCampaignModalOpen(false)} onCreateCampaign={handleCreateCampaign} data-id="element-517" />
    </div>;
};